/**
 * lib/gate.ts
 * ──────────────────────────────────────────────────────────────────────────
 * Configuration gates — "this screen cannot show you anything until the
 * workspace is configured".
 *
 * The keys below are the API's own, from
 * sk-company-cash-api/src/services/policy/gates.ts:
 *
 *   console        base currency + approval threshold are set (the whole app)
 *   categories     at least one active spend category exists
 *   variance_set   variance routing is set (shortfall → payroll, surplus → billing)
 *   connectors     BOTH payroll and billing are connected
 *   offboard_feed  the offboarding feed is on
 *
 * `true` means SATISFIED. The whole map arrives on GET /v1/config/bootstrap,
 * and a gate can also arrive mid-session as HTTP 412 + `details.gate` if
 * someone unsets one while a screen is open.
 *
 * The copy lives here so Reconciliation, the Sync log and Settlement alerts
 * cannot word the same gate three different ways.
 * ──────────────────────────────────────────────────────────────────────────
 */

/** The gate keys the API can report. */
export type GateKey = 'console' | 'categories' | 'variance_set' | 'connectors' | 'offboard_feed';

/** Which Settings card resolves a gate — the target of its amber-bar button. */
export type SettingsAnchor = 'features' | 'policy' | 'categories';

export interface GateCopy {
  /** The sentence in the amber bar. */
  message: string;
  /** The button that resolves it. */
  action: string;
  /** Where that button takes the user. */
  anchor: SettingsAnchor;
}

export const GATE_COPY: Record<GateKey, GateCopy> = {
  console: {
    message: 'Base currency and approval threshold are not set',
    action: 'Finish setup',
    anchor: 'policy',
  },
  categories: {
    message: 'No spend categories exist yet',
    action: 'Add spend categories',
    anchor: 'categories',
  },
  variance_set: {
    message: 'Variance routing is not set',
    action: 'Set variance routing',
    anchor: 'features',
  },
  connectors: {
    message: 'Payroll and Billing are not connected',
    action: 'Connect Payroll and Billing',
    anchor: 'features',
  },
  offboard_feed: {
    message: 'The offboarding feed is not connected',
    action: 'Connect the offboarding feed',
    anchor: 'features',
  },
};

/** The gate map from GET /v1/config/bootstrap. `true` means satisfied. */
export type GateState = Partial<Record<GateKey, boolean>>;

/** Is this screen blocked? Returns the gate to render, or null when clear. */
export function firstUnmet(gates: GateState, required: readonly GateKey[]): GateKey | null {
  return required.find((key) => gates[key] === false) ?? null;
}

/** Pull the gate out of a 412 response. Null for any other failure. */
export function gateFromError(error: unknown): GateKey | null {
  const err = error as { response?: { status?: number; data?: { details?: { gate?: string } } } };
  if (err?.response?.status !== 412) return null;
  const gate = err.response?.data?.details?.gate;
  return gate && gate in GATE_COPY ? (gate as GateKey) : null;
}

/**
 * Which gates each screen depends on. Screens read this rather than restating
 * it, so adding a gate to a screen is one line here.
 */
export const SCREEN_GATES = {
  overview: [],
  register: [],
  recon: ['variance_set'],
  approvals: [],
  router: ['connectors'],
  settle: ['offboard_feed'],
  settings: [],
  /** Not a screen — the spend modal, which cannot open without a category. */
  spend: ['categories'],
} as const satisfies Record<string, readonly GateKey[]>;
